import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanTextInput } from "@/components/ScanTextInput";
import { AuthImg } from "@/components/AuthImg";
import { tagStatusWords } from "@/lib/tagScan";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, PackagePlus, ShieldAlert } from "lucide-react";

// Suggested part name from a serial-tag spec key (human confirms/edits before create).
export const PART_NAME_FROM_SPEC = {
  engine_serial: "Engine", transmission_serial: "Transmission",
  differential_serial: "Differential", dump_box_serial: "Dump Box / Mount",
};

// Create a part FROM a truck — reuses the donor-capture primitive (immutable donor tie).
// The serial (if any) is copied onto the part; a physical tag is scanned; the part lands
// in the normal Sort & Identify queue. Human confirms name + tag before it's created.
export function CreatePartFromTruck({ open, onOpenChange, donorId, donorStock, specKey, suggestedName, serialValue, component, componentNumbers, condition, photoId, big = false, onCreated }) {
  const { has } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [tagCode, setTagCode] = useState("");
  const [serial, setSerial] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifyReasons, setVerifyReasons] = useState(null);   // A4: serial needs a human check before spawn
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (open) { setName(suggestedName || component || ""); setSerial(serialValue || ""); setTagCode(""); setVerifyReasons(null); setAck(false); }
  }, [open, specKey, suggestedName, serialValue, component]);

  if (!has("manage_parts")) return null;
  const nums = componentNumbers || [];

  const create = async () => {
    if (!tagCode.trim()) { toast.error("Scan or enter the FAS tag you're putting on this part."); return; }
    if (verifyReasons && !ack) { toast.error("Tick the box to confirm you've verified this serial."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/donors/${donorId}/capture`, {
        tag_code: tagCode.trim(), name: name.trim() || undefined, tag_serial: serial.trim() || undefined,
        component: component || undefined, photo_file_id: photoId || undefined, acknowledge_unverified: ack,
      });
      const moved = data.part?.numbers_moved;
      toast.success(`Part ${data.part?.tag_code || data.part?.short_code} created on ${donorStock || "truck"}${moved ? ` · ${moved} number(s) moved` : ""}`, {
        action: data.part?.id ? { label: "Open", onClick: () => navigate(`/parts/${data.part.id}`) } : undefined,
      });
      onOpenChange(false); onCreated?.(data.part);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "serial_needs_verify") {
        setVerifyReasons(detail.reasons || []); setAck(false);
        toast.warning(detail.message || "This serial needs a human check first.");
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="create-part-from-truck-dialog" className="rounded-lg w-[calc(100%-1.5rem)] sm:w-full">
        <DialogHeader><DialogTitle><PackagePlus className="w-5 h-5 inline text-[#B01E28] mr-1" />{component ? `Make this ${suggestedName || component} a part` : `Create a part from ${donorStock || "this truck"}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className={`${big ? "text-sm" : "text-xs"} text-slate-500`}>The new part is permanently tied to this truck (donor tie never breaks) and drops into the Identify queue. Scan the FAS tag you're attaching to it.</p>
          {photoId && (
            <div data-testid="cpt-component-photo" className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
              <AuthImg fileId={photoId} thumb alt="where its numbers were read" className={`${big ? "w-24 h-24" : "w-16 h-16"} rounded object-cover shrink-0`} />
              <span className={`${big ? "text-sm" : "text-[11px]"} text-slate-600`}>The truck photo its numbers were read from — check it's the one in front of you. It stays on the truck and goes with the part as a reference, not as its photo.</span>
            </div>
          )}
          {/* WHAT THE TRUCK SAID ABOUT THIS COMPONENT, visible at tagging time rather than buried.
              Known bad travels with the part as a flag plus the reason word; an "assessed OK" from
              the truck deliberately does NOT pre-fill the grade — the bench call is the one a
              customer buys against. */}
          {condition && condition.state !== "not_assessed" && (
            <div data-testid="cpt-inherited-condition"
              className={`rounded-lg border-2 p-2 ${condition.state === "present_bad" ? "border-[#B01E28] bg-red-50" : "border-slate-200 bg-slate-50"}`}>
              <div className={`text-[11px] font-bold uppercase tracking-wider ${condition.state === "present_bad" ? "text-[#B01E28]" : "text-slate-500"}`}>
                On the truck this was rated {condition.state_short}{condition.reason_label ? ` — ${condition.reason_label}` : ""}
              </div>
              <p className="text-[11px] text-slate-600 mt-0.5">
                {condition.state === "present_bad"
                  ? "That rides along: the part gets the reason word as a condition flag, and it sells as a core. It does NOT set the grade — you still grade it in hand."
                  : "Recorded as a note on the part. It does not set the grade — you still grade it in hand."}
                {condition.by ? ` (${condition.by})` : ""}
              </p>
            </div>
          )}
          {component && nums.length > 0 && (
            <div data-testid="cpt-moving-numbers" className="rounded-lg border border-amber-200 bg-amber-50 p-2">
              <div className="text-[11px] font-bold uppercase text-amber-700 mb-1">{nums.length} number(s) move to this part</div>
              <div className="flex flex-wrap gap-1.5">
                {nums.map((n, i) => <span key={i} className="text-[11px] font-mono bg-white border border-amber-200 rounded px-1.5 py-0.5 text-slate-700">{n.label}: {n.value}</span>)}
              </div>
              <p className="text-[10px] text-amber-600 mt-1">These leave the truck's search index and live on the part (no double-hits).</p>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Part</label>
            <Input data-testid="cpt-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Differential" className={`rounded-lg mt-1 ${big ? "h-14 text-lg" : "h-10"}`} />
          </div>
          {(serialValue !== undefined && !component) && (
            <div>
              <label className="text-xs font-bold uppercase text-slate-500">Serial / tag number{specKey ? " (read from the truck)" : ""}</label>
              <ScanTextInput testid="cpt-serial" value={serial} onChange={setSerial} placeholder="Serial number" inputClassName="h-10" className="mt-1" />
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">FAS tag <span className="text-[#B01E28]">*</span></label>
            <ScanTextInput testid="cpt-tag" autoFocus value={tagCode} onChange={setTagCode} resolveTag
              onResolved={(r) => { if (r.error) toast.error(r.error); else if (r.boundPart) toast.error(tagStatusWords(r)); }}
              placeholder="Scan or type the tag code" className="mt-1" inputClassName={big ? "h-14 text-lg" : undefined} onEnter={create} />
          </div>
        </div>
        {verifyReasons && (
          <div data-testid="cpt-verify-block" className="rounded-lg border-2 border-red-300 bg-red-50 p-3 mt-1">
            <div className="flex items-center gap-1.5 text-sm font-bold text-red-800 mb-1"><ShieldAlert className="w-4 h-4" />Verify this serial before spawning</div>
            <ul className="text-[11px] text-red-700 list-disc pl-4 space-y-0.5 mb-2">
              {verifyReasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            <label className="flex items-start gap-2 text-xs text-red-800 cursor-pointer">
              <input data-testid="cpt-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 accent-red-600" />
              <span>I've checked this serial against the physical tag / photo and it's correct. Spawn anyway (the part will stay flagged until verified so it can't be sold by mistake).</span>
            </label>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="cpt-create" onClick={create} disabled={busy || (verifyReasons && !ack)} className={`rounded-lg bg-[#B01E28] hover:bg-[#8B1820] w-full ${big ? "h-14 text-lg font-black" : ""}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PackagePlus className="w-4 h-4 mr-1" />}{verifyReasons ? "Verified — create part & tag it" : "Create part & tag it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
